import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Check,
  Copy,
  ExternalLink,
  Loader2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export interface DnsWizardDomain {
  id: string;
  domain: string;
  spfVerified: boolean;
  dkimVerified: boolean;
  returnPathVerified: boolean;
  verificationStatus: string;
  dkimHost: string | null;
  dkimTextValue: string | null;
  returnPathHost: string | null;
  returnPathCname: string | null;
}

interface DnsSetupWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain: DnsWizardDomain | null;
  /** Optional callback fired when verification flips to fully green. */
  onVerified?: () => void;
}

type RegistrarKey =
  | "godaddy"
  | "cloudflare"
  | "namecheap"
  | "google"
  | "route53"
  | "squarespace"
  | "other";

interface RegistrarInfo {
  label: string;
  panelUrl: string;
  /** Plain-English notes specific to the registrar's quirks. */
  notes: string[];
  /** True if the registrar requires the host to be entered WITHOUT the apex domain suffix. */
  stripsApex: boolean;
}

const REGISTRARS: Record<RegistrarKey, RegistrarInfo> = {
  godaddy: {
    label: "GoDaddy",
    panelUrl: "https://dcc.godaddy.com/control/portfolio",
    stripsApex: true,
    notes: [
      "GoDaddy expects the host name WITHOUT your domain — only the part before it. We've already trimmed each value below for you.",
      "Use the DNS Management page for your root domain, not the sending subdomain.",
      "After saving, GoDaddy can take 5–30 minutes to publish.",
    ],
  },
  cloudflare: {
    label: "Cloudflare",
    panelUrl: "https://dash.cloudflare.com/?to=/:account/:zone/dns",
    stripsApex: true,
    notes: [
      "Cloudflare expects the host name WITHOUT your domain. We've already trimmed each value below.",
      "On the CNAME record, set the proxy status to \"DNS only\" (gray cloud). Proxied (orange cloud) breaks email DNS.",
      "Cloudflare publishes new records almost instantly.",
    ],
  },
  namecheap: {
    label: "Namecheap",
    panelUrl: "https://ap.www.namecheap.com/Domains/DomainControlPanel/",
    stripsApex: true,
    notes: [
      "Use the \"Advanced DNS\" tab on your root domain.",
      "Namecheap expects the host name WITHOUT your domain — we've trimmed each value below for you.",
      "Allow up to 30 minutes for new records to publish.",
    ],
  },
  google: {
    label: "Google Domains / Squarespace Domains",
    panelUrl: "https://domains.squarespace.com/",
    stripsApex: true,
    notes: [
      "Google Domains migrated to Squarespace. Use the same login.",
      "Use the host WITHOUT your domain — already trimmed below.",
      "Records typically publish within a few minutes.",
    ],
  },
  route53: {
    label: "AWS Route 53",
    panelUrl: "https://console.aws.amazon.com/route53/v2/hostedzones",
    stripsApex: false,
    notes: [
      "Route 53 expects the FULL host (including your domain). The values shown below are already in the correct full-host form.",
      "Open the hosted zone for your root domain and create a new record for each item.",
      "Route 53 publishes records within 60 seconds.",
    ],
  },
  squarespace: {
    label: "Squarespace",
    panelUrl: "https://account.squarespace.com/domains",
    stripsApex: true,
    notes: [
      "Use the \"Custom Records\" panel under your domain's DNS settings.",
      "Use the host WITHOUT your domain — we've trimmed each value below.",
      "Allow up to 30 minutes for records to publish.",
    ],
  },
  other: {
    label: "Other registrar",
    panelUrl: "",
    stripsApex: false,
    notes: [
      "Most registrars accept either the short host (without your domain) or the full host. We've shown both — use whichever your registrar's form expects.",
      "DNS changes can take up to 48 hours to fully propagate, but most publish in under an hour.",
    ],
  },
};

function trimApex(host: string, apex: string): string {
  // Strip ".<apex>" from the end of host. If host equals apex, return "@".
  if (host === apex) return "@";
  if (host.endsWith(`.${apex}`)) return host.slice(0, -1 - apex.length);
  return host;
}

/** Best-effort apex-domain extraction (for trimming display hosts). */
function apexOf(domain: string): string {
  // For "send.acme.com" we want "acme.com"; for "acme.com" we keep "acme.com".
  const parts = domain.split(".").filter(Boolean);
  if (parts.length <= 2) return domain;
  return parts.slice(-2).join(".");
}

interface DnsRecordSpec {
  key: "spf" | "dkim" | "returnpath";
  type: "TXT" | "CNAME";
  label: string;
  fullHost: string;
  shortHost: string;
  value: string;
  /** Live verification status from the server. */
  verified: boolean;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-7 px-2"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          toast({ title: `${label} copied` });
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast({ title: "Copy failed", description: "Select and copy manually.", variant: "destructive" });
        }
      }}
      data-testid={`button-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}

export function DnsSetupWizard({ open, onOpenChange, domain, onVerified }: DnsSetupWizardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [registrar, setRegistrar] = useState<RegistrarKey>("godaddy");
  const [acked, setAcked] = useState<Record<string, boolean>>({});

  // Reset wizard whenever a different domain is opened.
  useEffect(() => {
    if (open) {
      setStep(1);
      setAcked({});
    }
  }, [open, domain?.id]);

  const apex = useMemo(() => (domain ? apexOf(domain.domain) : ""), [domain]);
  const registrarInfo = REGISTRARS[registrar];

  const records = useMemo<DnsRecordSpec[]>(() => {
    if (!domain) return [];
    const list: DnsRecordSpec[] = [];

    // SPF TXT (Postmark-standard include).
    const spfHost = domain.domain;
    list.push({
      key: "spf",
      type: "TXT",
      label: "SPF (TXT)",
      fullHost: spfHost,
      shortHost: trimApex(spfHost, apex),
      value: "v=spf1 include:spf.mtasv.net ~all",
      verified: domain.spfVerified,
    });

    // DKIM TXT (provider-issued).
    if (domain.dkimHost && domain.dkimTextValue) {
      list.push({
        key: "dkim",
        type: "TXT",
        label: "DKIM (TXT)",
        fullHost: domain.dkimHost,
        shortHost: trimApex(domain.dkimHost, apex),
        value: domain.dkimTextValue,
        verified: domain.dkimVerified,
      });
    }

    // Return-Path CNAME (provider-issued).
    if (domain.returnPathHost && domain.returnPathCname) {
      list.push({
        key: "returnpath",
        type: "CNAME",
        label: "Return-Path (CNAME)",
        fullHost: domain.returnPathHost,
        shortHost: trimApex(domain.returnPathHost, apex),
        value: domain.returnPathCname,
        verified: domain.returnPathVerified,
      });
    }

    return list;
  }, [domain, apex]);

  const allAcked = records.length > 0 && records.every((r) => acked[r.key]);
  const allVerified = records.length > 0 && records.every((r) => r.verified);

  const verifyMutation = useMutation({
    mutationFn: async () => {
      if (!domain) throw new Error("No domain");
      const res = await apiRequest("POST", `/api/admin/sending-domains/${domain.id}/verify`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/sending-domains"] });
    },
    onError: (err: any) => {
      toast({
        title: "Verification check failed",
        description: err?.message || "Try again in a few minutes — DNS can take time to publish.",
        variant: "destructive",
      });
    },
  });

  // Auto-fire onVerified when we hit fully green.
  useEffect(() => {
    if (open && step === 3 && allVerified) {
      onVerified?.();
    }
  }, [open, step, allVerified, onVerified]);

  if (!domain) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-dns-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            DNS Setup Wizard
          </DialogTitle>
          <DialogDescription>
            We'll walk you through publishing the DNS records for{" "}
            <span className="font-mono text-foreground">{domain.domain}</span>. Takes about 5 minutes.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium ${
                  step === n
                    ? "bg-primary text-primary-foreground"
                    : step > n
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {step > n ? <Check className="w-3 h-3" /> : n}
              </div>
              <span className={step === n ? "font-medium" : "text-muted-foreground"}>
                {n === 1 ? "Pick registrar" : n === 2 ? "Add records" : "Verify"}
              </span>
              {n < 3 && <div className="flex-1 h-px bg-border ml-1" />}
            </div>
          ))}
        </div>

        {/* STEP 1 — Registrar */}
        {step === 1 && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm">Where is <span className="font-mono">{apex}</span> registered?</Label>
              <Select value={registrar} onValueChange={(v) => setRegistrar(v as RegistrarKey)}>
                <SelectTrigger data-testid="select-registrar">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(REGISTRARS) as RegistrarKey[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      {REGISTRARS[k].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Don't know? It's the company you bought your domain from. Check your billing or whois.
              </p>
            </div>

            <Card className="bg-muted/40">
              <CardContent className="py-3 space-y-2">
                <div className="text-sm font-medium">Tips for {registrarInfo.label}</div>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  {registrarInfo.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
                {registrarInfo.panelUrl && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-1"
                    onClick={() => window.open(registrarInfo.panelUrl, "_blank", "noopener")}
                    data-testid="button-open-registrar"
                  >
                    Open {registrarInfo.label} <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setStep(2)}
                data-testid="button-wizard-next-1"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2 — Records */}
        {step === 2 && (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              In {registrarInfo.label}'s DNS panel, add each record below. Click the copy icon next to each value.
            </p>

            {records.length === 0 && (
              <Card>
                <CardContent className="py-4 text-sm text-muted-foreground text-center">
                  Records aren't ready yet. Close this wizard and try again in a moment.
                </CardContent>
              </Card>
            )}

            {records.map((r) => {
              const displayHost = registrarInfo.stripsApex ? r.shortHost : r.fullHost;
              return (
                <Card key={r.key} data-testid={`card-record-${r.key}`}>
                  <CardContent className="py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{r.type}</Badge>
                        <span className="text-sm font-medium">{r.label}</span>
                      </div>
                      {r.verified && (
                        <Badge variant="outline" className="text-green-600 border-green-500/30">
                          <Check className="w-3 h-3 mr-1" /> Live
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Host / Name</div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-muted/50 px-2 py-1.5 rounded break-all font-mono" data-testid={`text-host-${r.key}`}>
                            {displayHost}
                          </code>
                          <CopyButton value={displayHost} label={`${r.label} host`} />
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-muted-foreground uppercase tracking-wide">
                          {r.type === "CNAME" ? "Points to" : "Value"}
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-muted/50 px-2 py-1.5 rounded break-all font-mono" data-testid={`text-value-${r.key}`}>
                            {r.value}
                          </code>
                          <CopyButton value={r.value} label={`${r.label} value`} />
                        </div>
                      </div>
                    </div>

                    <label className="flex items-center gap-2 pt-1 cursor-pointer">
                      <Checkbox
                        checked={!!acked[r.key]}
                        onCheckedChange={(v) => setAcked((p) => ({ ...p, [r.key]: v === true }))}
                        data-testid={`checkbox-acked-${r.key}`}
                      />
                      <span className="text-xs text-muted-foreground">I've added this record in {registrarInfo.label}</span>
                    </label>
                  </CardContent>
                </Card>
              );
            })}

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="button-wizard-back-2">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button
                onClick={() => {
                  setStep(3);
                  // Kick off a verification check immediately when entering step 3.
                  verifyMutation.mutate();
                }}
                disabled={!allAcked || records.length === 0}
                data-testid="button-wizard-next-2"
              >
                Check DNS <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — Verify */}
        {step === 3 && (
          <div className="space-y-3 py-2">
            {verifyMutation.isPending ? (
              <Card>
                <CardContent className="py-6 flex flex-col items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Checking DNS for {domain.domain}…
                </CardContent>
              </Card>
            ) : allVerified ? (
              <Card className="border-green-500/30 bg-green-500/5">
                <CardContent className="py-5 flex flex-col items-center gap-2 text-center">
                  <div className="w-10 h-10 rounded-full bg-green-500/15 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-base font-medium">All records are live!</div>
                  <div className="text-xs text-muted-foreground max-w-md">
                    {domain.domain} is verified and ready to send. You can close this wizard.
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="border-amber-500/30 bg-amber-500/5">
                  <CardContent className="py-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-xs text-muted-foreground">
                      Some records aren't visible yet. DNS can take a few minutes to a few hours to publish — most
                      registrars publish within 30 minutes. Click <span className="font-medium">Check again</span> below.
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-2">
                  {records.map((r) => (
                    <div
                      key={r.key}
                      className="flex items-center justify-between p-2 rounded border bg-card"
                      data-testid={`status-${r.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{r.type}</Badge>
                        <span className="text-sm">{r.label}</span>
                      </div>
                      {r.verified ? (
                        <Badge variant="outline" className="text-green-600 border-green-500/30">
                          <Check className="w-3 h-3 mr-1" /> Found
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-500/30">
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Not yet
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="button-wizard-back-3">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to records
              </Button>
              {allVerified ? (
                <Button onClick={() => onOpenChange(false)} data-testid="button-wizard-done">
                  Done
                </Button>
              ) : (
                <Button
                  onClick={() => verifyMutation.mutate()}
                  disabled={verifyMutation.isPending}
                  data-testid="button-wizard-recheck"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${verifyMutation.isPending ? "animate-spin" : ""}`} />
                  Check again
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
